import type { SiteContentKey } from '@pms/shared';
import { UNIQUE_FEATURES } from '../../data/landing';
import { Reveal, useCountUp, parseStat } from '../../lib/motion';
import { useSiteContent, resolveStat } from '../../hooks/useSiteContent';

/** The three figures in the stat strip, in display order. Values + labels are
 *  super-admin editable (with hardcoded fallback defaults). */
const DIFFERENCE_KEYS: SiteContentKey[] = [
  'difference.rating',
  'difference.hotels',
  'difference.cities',
];

const featureImg =
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=1100&q=80';

/**
 * "The BestAcco difference" — an asymmetric editorial band on a cream-deep ground.
 * Image rail on the left, prose + a small principles list on the right, with a
 * thin stat strip beneath. No card grid; deliberate whitespace and large type.
 */
export function BrandDifference() {
  const { data } = useSiteContent();
  return (
    <section aria-labelledby="difference-h2" className="relative grain bg-cream-deep">
      <div className="px-6 py-20 sm:px-10 lg:px-16 lg:py-28 2xl:px-24">
        <div className="grid items-stretch gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          {/* Image rail — fills the column height so the columns stay balanced */}
          <Reveal className="relative h-full">
            <div className="group h-full min-h-[26rem] overflow-hidden rounded-[1.75rem] rounded-tr-[5rem]">
              <img
                src={featureImg}
                alt="A characterful hotel lounge with vintage furniture, books and warm lamplight"
                className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            </div>
            <span className="absolute -bottom-5 left-6 rounded-full bg-clay px-5 py-2.5 font-serif text-sm font-medium text-white shadow-lg sm:left-10">
              Chosen by hand, not by algorithm
            </span>
          </Reveal>

          {/* Prose + principles */}
          <Reveal delay={120}>
            <h2
              id="difference-h2"
              className="text-balance font-serif text-[2.2rem] font-black leading-[1.0] tracking-tight text-ink sm:text-[2.7rem]"
            >
              Hotels we would actually stay in.
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft">
              Most travel sites list everything and rank by who pays most. We do
              the opposite. Every hotel here earns its place: for its design, its
              hospitality and its sense of where it is. The result is a smaller
              world, but a far more interesting one.
            </p>

            <dl className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
              {UNIQUE_FEATURES.map((f) => (
                <div key={f.id} className="border-l-2 border-sand pl-4">
                  <dt className="font-serif text-xl font-semibold text-ink">{f.title}</dt>
                  <dd className="mt-1.5 text-[15px] leading-relaxed text-ink-muted">{f.description}</dd>
                </div>
              ))}
            </dl>

            {/* Stat strip — sits under the principles, filling the column */}
            <div className="mt-12 grid grid-cols-3 gap-4 border-t border-sand pt-8 sm:gap-6">
              {DIFFERENCE_KEYS.map((key) => {
                const stat = resolveStat(data, key);
                return <Stat key={key} value={stat.value} label={stat.label} />;
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stat({ value: raw, label }: { value: string; label: string }) {
  const { value, suffix, decimals } = parseStat(raw);
  const { ref, display } = useCountUp<HTMLParagraphElement>(value, { suffix, decimals });
  return (
    <div>
      <p
        ref={ref}
        className="font-serif text-3xl font-semibold text-clay sm:text-4xl lg:text-5xl"
      >
        {display}
      </p>
      <p className="mt-1 text-sm uppercase tracking-wide text-ink-muted">{label}</p>
    </div>
  );
}
